"use client";

import { DrupalNode } from "next-drupal";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { IoClose } from "react-icons/io5";
import CommonMultiCheckox from "@/components/sections/CommonMultiCheckbox";
import FloatingButton from "@/components/ui/FloatingButton";
import { IoMdImages } from "react-icons/io";
import ReactPlayer from "react-player";
import Banner from "@/components/sections/Banner";
import { config } from "@/lib/config";
import { resolveLink } from "@/utils";

interface SectorScreenProps {
  data: DrupalNode[];
}

export default function SectorScreen({ data }: SectorScreenProps) {
  const searchParams = useSearchParams();
  const sector = searchParams.get("sector");
  const router = useRouter();
  const path = usePathname();
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);
  const [showClearBtn, setShowClearBtn] = useState<boolean>(false);
  const [isAboveMobile, setAboveMobile] = useState<boolean>(false);
  const [selectedTopic, setSelectedTopic] = useState<string[]>([]);
  const [selectedResource, setSelectedResource] = useState<string[]>([]);
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [openIndex, setOpenIndex] = useState<{
    [key: number]: boolean;
  }>({ 0: true, 1: false, 2: false });

  const toSlug = (str: string) =>
    str
      ?.toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-") // replace non-alphanumeric with -
      .replace(/^-+|-+$/g, "");

  useEffect(() => {
    if (!sector) return;
    if (sector === "elevate" || sector === "support" || sector === "invest") {
      setSelectedTopic([toSlug(sector)]);
      setOpenIndex({ 0: false, 1: true, 2: false });
      setShowClearBtn(true);
    } else {
      setSelectedSectors([toSlug(sector)]);
      setShowClearBtn(true);
    }
  }, [sector]);

  // Collect unique categories
  const topicData = useMemo(() => {
    const categories = data
      .map((item) => item?.field_cs_category && item?.field_cs_category.trim())
      .filter(Boolean);
    const uniqueCategories = Array.from(new Set(categories));
    const result = uniqueCategories.filter((item) =>
      ["invest", "support", "elevate"].includes(item),
    );
    return result.map((category) => ({ name: category }));
  }, [data]);

  // Collect unique themes for Resources filter
  const resourceData = useMemo(() => {
    const themes = data
      .map((item) => item?.field_cs_theme && item?.field_cs_theme.trim())
      .filter(Boolean);
    const uniqueThemes = Array.from(new Set(themes));
    return uniqueThemes.map((theme) => ({ name: theme }));
  }, [data]);

  const sectorData = useMemo(() => {
    const sectors = data
      .map((item) => item?.field__cs_sectors && item?.field__cs_sectors.trim())
      .filter(Boolean);
    const uniqueSectors = Array.from(new Set(sectors));
    return uniqueSectors.map((sector) => ({ name: sector }));
  }, [data]);

  const filteredData = useMemo(() => {
    let filtered = data;

    // Apply sectors filter
    if (selectedSectors.length > 0) {
      filtered = filtered.filter((item) =>
        selectedSectors.some(
          (sector) => toSlug(item?.field__cs_sectors?.trim() || "") === sector,
        ),
      );
    }

    // Apply category filter
    if (selectedTopic.length > 0) {
      filtered = filtered.filter((item) =>
        selectedTopic.some(
          (topic) => toSlug(item?.field_cs_category?.trim() || "") === topic,
        ),
      );
    }

    // Apply resources filter
    if (selectedResource.length > 0) {
      filtered = filtered.filter((item) =>
        selectedResource.some(
          (theme) => toSlug(item?.field_cs_theme?.trim() || "") === theme,
        ),
      );
    }

    return filtered;
  }, [data, selectedSectors, selectedTopic, selectedResource]);

  const handleToggleOpen = (index: number) => {
    setOpenIndex({ ...openIndex, [index]: !Boolean(openIndex[index]) });
  };

  const makeTopicOptions = () => {
    const options = topicData?.map((data) => {
      const formattedName = data.name
        .split(/[-_\s]+/)
        .map(
          (word: string) =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ");

      return {
        value: toSlug(data.name),
        label: formattedName,
      };
    });
    return options;
  };

  const makeResourceOptions = () => {
    return resourceData?.map((data) => ({
      value: toSlug(data.name),
      label: data.name,
    }));
  };

  const handleTopicSelect = (value: string[]) => {
    setSelectedTopic(value);
    setShowClearBtn(
      value.length > 0 ||
        selectedResource.length > 0 ||
        selectedSectors.length > 0,
    );
  };

  const handleResourceSelect = (value: string[]) => {
    setSelectedResource(value);
    setShowClearBtn(
      value.length > 0 ||
        selectedTopic.length > 0 ||
        selectedSectors.length > 0,
    );
  };

  const handleSectorSelect = (value: string[]) => {
    setSelectedSectors(value);
    setShowClearBtn(
      value.length > 0 ||
        selectedTopic.length > 0 ||
        selectedResource.length > 0,
    );
  };

  const handleClearFilters = () => {
    setSelectedTopic([]);
    setSelectedResource([]);
    setSelectedSectors([]);
    setShowClearBtn(false);
    if (isAboveMobile) {
      window.scrollTo({ top: 400, behavior: "smooth" });
    }
  };

  return (
    <div className="pt-20 bg-mapGray filter-page-height">
      <Banner
        title={"India Resources"}
        isRounded
        backgroundImg="/static/images/india.webp"
      />
      <FloatingButton
        onClick={() => setShowMobileFilters(!showMobileFilters)}
      />

      <div className="flex justify-between py-[60px] betweenMobileTab:pt-14 betweenMobileTab:pb-14 mobileMax:pb-12 mobileMax:pt-6 px-8 lieTablets:px-2 largeDesk:px-2 mobileMax:flex-wrap mobileMax:px-[12px]">
        {showMobileFilters && (
          <div
            className="hidden lieTablets:block fixed h-full w-full bg-blackHighOpacity z-[4] top-0 right-0"
            onClick={() => setShowMobileFilters(false)}
          />
        )}

        <div
          className={`${
            showMobileFilters
              ? "lieTablets:w-[300px] lieTablets:left-0 lieTablets:top-0 fixed left-0 top-0 flex-col mb-0 !z-50 w-full h-full dark-filter-shadow !block overflow-auto"
              : "sticky top-20 left-0 mr-2 px-5  w-[25%] desktopLg:w-[20%]"
          } aboveLaptop:w-[30%] mobileMax:w-full z-[3] h-full hidden exactLaptop:block`}
        >
          <div className="h-full bg-mapGray w-full laptopMax:h-full laptopMax:overflow-auto">
            <div className="flex laptopMax:px-3 text-black items-center justify-between min-h-[55px] laptopMax:min-h-[82px] pt-[20px] laptopMax:pt-[40px] laptopMax:pb-2 laptopMax:sticky laptopMax:top-0 laptopMax:z-[3] bg-mapGray">
              <IoClose
                className="exactLaptop:hidden text-odd text-black absolute right-2.5 top-1.5 cursor-pointer"
                onClick={() => setShowMobileFilters(false)}
              />
              <h5 className="--font-poppins text-medium leading-normal mobileMax:text-small font-bold">
                Filter by:
              </h5>
              <div className="flex items-center">
                {showClearBtn && (
                  <button
                    className="block capitalize rounded-full ml-2 px-5 largeDesk:px-3 py-1 border border-borderColor mobileMax:text-xsmall text-small --font-poppins flex items-center justify-center cursor-pointer"
                    onClick={handleClearFilters}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="w-full laptopMax:px-3 custom-react-combbox">
              <div className="bg-white commonBoxShadow w-full mt-5 mobileMax:mt-3 border-box max-h-[75vh] lieTablets:max-h-[80vh] mobileMax:max-h-[80vh] overflow-auto">
                <div className="w-full">
                  {/* Sectors Filter */}
                  <CommonMultiCheckox
                    menuTitle="Sectors"
                    value={selectedSectors}
                    list={sectorData?.map((data) => ({
                      value: toSlug(data.name),
                      label: data.name,
                    }))}
                    onSelectChange={handleSectorSelect}
                    menuListHeight={130}
                    isOpen={Boolean(openIndex[0])}
                    toggleOpen={() => handleToggleOpen(0)}
                  />
                </div>
                <div className="w-full">
                  {/* Category Filter */}
                  <CommonMultiCheckox
                    menuTitle="Category"
                    value={selectedTopic}
                    list={makeTopicOptions()}
                    onSelectChange={handleTopicSelect}
                    menuListHeight={130}
                    isOpen={Boolean(openIndex[1])}
                    toggleOpen={() => handleToggleOpen(1)}
                  />
                </div>
                <div className="w-full">
                  {/* Resources Filter */}
                  <CommonMultiCheckox
                    menuTitle="Resources"
                    value={selectedResource}
                    list={makeResourceOptions()}
                    onSelectChange={handleResourceSelect}
                    menuListHeight={130}
                    isOpen={Boolean(openIndex[2])}
                    toggleOpen={() => handleToggleOpen(2)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results section */}
        <div className="pt-[20px] w-full">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0 }}
            className="--font-poppins text-medium leading-normal mobileMax:text-small font-bold px-[15px] mobileMax:px-0 mb-6 text-black"
          >
            {filteredData?.length}
            <span className="ml-2">results</span>
          </motion.div>

          <div className="flex justify-center desktop:justify-start flex-wrap gap-6 px-4 mobileMax:px-0">
            {filteredData?.map((card: DrupalNode, index: number) => {
              const isVideo =
                card?.field_cs_content_link?.[0]?.uri?.includes(
                  "youtube.com",
                ) ||
                card?.field_cs_content_link?.[0]?.uri?.includes("youtu.be");
              const backgroundImage = card?.field_cs_image?.uri?.url;

              const handleClick = (e: React.MouseEvent) => {
                if (card?.field_cs_landing_page === "yes") {
                  e.preventDefault();
                  router.push(`${path}/${card?.id}`);
                }
              };

              return (
                <Link
                  key={card?.id || index}
                  href={resolveLink(card?.field_cs_content_link?.[0]?.uri)}
                  onClick={handleClick}
                  target="_blank"
                  className="w-[30%] min-w-[340px] relative overflow-hidden group rounded-xl transition-all duration-700 ease-in-out hover:scale-105 hover:shadow-lg block h-80 md:h-96 bg-white"
                >
                  {/* Theme Badge */}
                  {(card?.field_cs_theme || card?.field__cs_sectors) && (
                    <div className="absolute top-4 left-4 z-[1] flex gap-2 flex-wrap text-black">
                      {card?.field_cs_theme && (
                        <span className="bg-white/90 text-gray-800 px-3 py-1 text-sm inline-block font-semibold rounded">
                          {card?.field_cs_theme}
                        </span>
                      )}
                      {card?.field__cs_sectors && (
                        <span className="bg-white/90 text-gray-800 px-3 py-1 text-sm inline-block font-semibold rounded">
                          {card?.field__cs_sectors}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Background Image */}
                  {!isVideo && backgroundImage && (
                    <div
                      className="absolute inset-0 w-full h-full"
                      style={{
                        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.2)), url(${config.apiBase}${backgroundImage})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                      }}
                    />
                  )}

                  {/* Placeholder for no image */}
                  {!isVideo && !backgroundImage && (
                    <div className="absolute inset-0 w-full h-full bg-graybg flex items-center justify-center">
                      <IoMdImages className="text-gray-400 w-16 h-16" />
                    </div>
                  )}

                  {/* Video */}
                  {isVideo && (
                    <div className="absolute inset-0 w-full h-full">
                      <ReactPlayer
                        url={card?.field_cs_content_link?.[0]?.uri || ""}
                        width="100%"
                        height="100%"
                        light={true}
                        controls={true}
                      />
                    </div>
                  )}

                  {/* Content Overlay */}
                  <div className="absolute bottom-0 left-0 p-4 md:p-6 w-full z-10 bg-gradient-to-t from-black/60 to-transparent">
                    <div className="max-w-full">
                      <h3
                        className={`${
                          backgroundImage || isVideo
                            ? "text-white"
                            : "text-gray-800"
                        } font-bold !text-[20px] leading-tight mb-2`}
                      >
                        {card?.title || `Untitled ${index + 1}`}
                      </h3>
                    </div>
                  </div>

                  {/* Hover overlay */}
                  {/* <div className="absolute inset-0 bg-[#000000d4] opacity-0 group-hover:opacity-100 transition-opacity duration-300" /> */}
                </Link>
              );
            })}
          </div>

          {/* No results message */}
          {filteredData?.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                No results found for the selected filters
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
